import React, { useState, useEffect } from 'react';
import { Template, UpdateTemplateRequest } from '../types';
import { apiService } from '../services/api';
import './TemplatesList.css';

const TemplatesList: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPattern, setEditPattern] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.listTemplates();
      setTemplates(response.templates);
    } catch (error) {
      console.error('Error loading templates:', error);
      setError(error instanceof Error ? error.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template: Template) => {
    setEditingId(template.id);
    setEditPattern(template.pattern);
  };

  const handleSaveEdit = async (templateId: string) => {
    if (!editPattern.trim()) {
      alert('Pattern cannot be empty');
      return;
    }

    try {
      const updates: UpdateTemplateRequest = {
        pattern: editPattern.trim(),
      };

      const updatedTemplate = await apiService.updateTemplate(templateId, updates);
      
      setTemplates(templates.map(t => 
        t.id === templateId ? updatedTemplate : t
      ));
      
      setEditingId(null);
      setEditPattern('');
    } catch (error) {
      console.error('Error updating template:', error);
      alert(error instanceof Error ? error.message : 'Failed to update template');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditPattern('');
  };

  const handleDelete = async (templateId: string, pattern: string) => {
    if (!window.confirm(`Are you sure you want to delete the template "${pattern}"?`)) {
      return;
    }

    try {
      await apiService.deleteTemplate(templateId);
      setTemplates(templates.filter(t => t.id !== templateId));
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(error instanceof Error ? error.message : 'Failed to delete template');
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.pattern.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.section_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.category_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="templates-container">
        <div className="loading-message">Loading templates...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="templates-container">
        <div className="error-message">{error}</div>
        <button onClick={loadTemplates} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="templates-container">
      <div className="templates-header">
        <h2>Template Management</h2>
        <p className="templates-description">
          Manage your auto-categorization templates. These patterns are used to automatically 
          assign sections and categories to new transactions during CSV parsing.
        </p>
      </div>

      {/* Search/Filter */}
      <div className="templates-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search templates by pattern, section, or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="templates-count">
          {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
          {searchQuery && ` (filtered from ${templates.length})`}
        </div>
      </div>

      {/* Templates List */}
      {filteredTemplates.length === 0 ? (
        <div className="empty-state">
          {searchQuery ? (
            <p>No templates match your search.</p>
          ) : (
            <div>
              <p>No templates created yet.</p>
              <p>Create templates by selecting section/category on transactions and clicking "Create Template".</p>
            </div>
          )}
        </div>
      ) : (
        <div className="templates-list">
          {filteredTemplates.map((template) => (
            <div key={template.id} className="template-item">
              <div className="template-pattern">
                {editingId === template.id ? (
                  <div className="edit-pattern-container">
                    <input
                      type="text"
                      value={editPattern}
                      onChange={(e) => setEditPattern(e.target.value)}
                      className="edit-pattern-input"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveEdit(template.id);
                        } else if (e.key === 'Escape') {
                          handleCancelEdit();
                        }
                      }}
                    />
                    <div className="edit-actions">
                      <button
                        onClick={() => handleSaveEdit(template.id)}
                        className="save-edit-button"
                        title="Save changes"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="cancel-edit-button"
                        title="Cancel editing"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ) : (
                  <span className="pattern-text">"{template.pattern}"</span>
                )}
              </div>

              <div className="template-categorization">
                <span className="section-name">{template.section_name}</span>
                <span className="separator">→</span>
                <span className="category-name">{template.category_name}</span>
              </div>

              <div className="template-metadata">
                <span className="created-date">Created: {formatDate(template.created_at)}</span>
                {template.updated_at !== template.created_at && (
                  <span className="updated-date">Updated: {formatDate(template.updated_at)}</span>
                )}
              </div>

              <div className="template-actions">
                {editingId === template.id ? null : (
                  <>
                    <button
                      onClick={() => handleEdit(template)}
                      className="edit-template-button"
                      title="Edit pattern"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(template.id, template.pattern)}
                      className="delete-template-button"
                      title="Delete template"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Refresh Button */}
      <div className="templates-footer">
        <button onClick={loadTemplates} className="refresh-button">
          Refresh Templates
        </button>
      </div>
    </div>
  );
};

export default TemplatesList;